import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { PhCaretUp, PhCaretDown } from 'ember-phosphor-icons';
import type Owner from '@ember/owner';

interface ExpandableSectionSignature {
  Args: {
    title: string;
    defaultExpanded?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

/**
 * Expandable Section Component
 * Provides a collapsible section with a header and content area
 * Used in drawers to group related content that can be shown/hidden
 */
class ExpandableSection extends Component<ExpandableSectionSignature> {
  @tracked isExpanded: boolean;

  constructor(owner: Owner, args: ExpandableSectionSignature['Args']) {
    super(owner, args);
    this.isExpanded = args.defaultExpanded ?? true;
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  <template>
    <div class="flex flex-col gap-4 w-full" ...attributes>
      {{! Section Header }}
      <button
        type="button"
        class="flex items-center justify-between w-full cursor-pointer"
        {{on "click" this.toggleExpanded}}
        aria-expanded={{if this.isExpanded "true" "false"}}
      >
        <span class="text-lg font-semibold text-gray-700 leading-relaxed">
          {{@title}}
        </span>
        {{#if this.isExpanded}}
          <PhCaretUp @size={{24}} class="text-gray-500 shrink-0" />
        {{else}}
          <PhCaretDown @size={{24}} class="text-gray-500 shrink-0" />
        {{/if}}
      </button>

      {{! Section Content }}
      {{#if this.isExpanded}}
        <div class="pr-10">
          {{yield}}
        </div>
      {{/if}}
    </div>
  </template>
}

export default ExpandableSection;
